import { useEffect } from 'react'

export interface KeyboardShortcut {
  key: string // Keyboard key to match
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
  cmd?: boolean
  callback: (e: KeyboardEvent) => void
}

interface ShortcutConfig {
  shortcuts: KeyboardShortcut[]
}

export function useKeyboardShortcuts(config: ShortcutConfig) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      config.shortcuts.forEach((shortcut) => {
        const matchesKey = e.key.toLowerCase() === shortcut.key.toLowerCase()
        const matchesCtrl = shortcut.ctrl === (e.ctrlKey || e.metaKey)
        const matchesShift = shortcut.shift === e.shiftKey
        const matchesAlt = shortcut.alt === e.altKey
        const matchesCmd =
          (shortcut.cmd === undefined) || (shortcut.cmd === (e.metaKey || e.ctrlKey))

        if (
          matchesKey &&
          matchesCtrl &&
          matchesShift &&
          matchesAlt &&
          matchesCmd
        ) {
          e.preventDefault()
          shortcut.callback(e)
        }
      })
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [config.shortcuts])
}

export function useCommandK(onOpen: () => void) {
  useKeyboardShortcuts({
    shortcuts: [
      {
        key: 'k',
        ctrl: true,
        callback: (e) => {
          e.preventDefault()
          onOpen()
        },
      },
      {
        key: 'k',
        cmd: true,
        callback: (e) => {
          e.preventDefault()
          onOpen()
        },
      },
    ],
  })
}

export function useGlobalShortcuts(callbacks: {
  onCommandPalette?: () => void
  onSave?: () => void
  onUndo?: () => void
  onRedo?: () => void
  onSendMessage?: () => void
  onEscape?: () => void
  onDelete?: () => void
  onTest?: () => void
  onPromote?: () => void
}) {
  useKeyboardShortcuts({
    shortcuts: [
      // Cmd/Ctrl+K: Command Palette
      ...(callbacks.onCommandPalette
        ? [
            {
              key: 'k' as const,
              ctrl: true,
              callback: callbacks.onCommandPalette,
            },
          ]
        : []),

      // Cmd/Ctrl+S: Save
      ...(callbacks.onSave
        ? [
            {
              key: 's' as const,
              ctrl: true,
              callback: callbacks.onSave,
            },
          ]
        : []),

      // Cmd/Ctrl+Z: Undo
      ...(callbacks.onUndo
        ? [
            {
              key: 'z' as const,
              ctrl: true,
              callback: callbacks.onUndo,
            },
          ]
        : []),

      // Cmd/Ctrl+Shift+Z: Redo
      ...(callbacks.onRedo
        ? [
            {
              key: 'z' as const,
              ctrl: true,
              shift: true,
              callback: callbacks.onRedo,
            },
          ]
        : []),

      // Cmd/Ctrl+Enter: Send Message
      ...(callbacks.onSendMessage
        ? [
            {
              key: 'Enter' as const,
              ctrl: true,
              callback: callbacks.onSendMessage,
            },
          ]
        : []),

      // Escape: Close/Deselect
      ...(callbacks.onEscape
        ? [
            {
              key: 'Escape' as const,
              callback: callbacks.onEscape,
            },
          ]
        : []),

      // Delete/Backspace: Delete Selected
      ...(callbacks.onDelete
        ? [
            {
              key: 'Delete' as const,
              callback: callbacks.onDelete,
            },
            {
              key: 'Backspace' as const,
              callback: callbacks.onDelete,
            },
          ]
        : []),

      // Cmd/Ctrl+T: Test
      ...(callbacks.onTest
        ? [
            {
              key: 't' as const,
              ctrl: true,
              callback: callbacks.onTest,
            },
          ]
        : []),

      // Cmd/Ctrl+P: Promote
      ...(callbacks.onPromote
        ? [
            {
              key: 'p' as const,
              ctrl: true,
              callback: callbacks.onPromote,
            },
          ]
        : []),
    ],
  })
}
